'use es6';

import tinymce from 'tinymce';
import ReactDOM from 'react-dom';
import http from 'hub-http/clients/apiClient';
import PortalIdParser from 'PortalIdParser';
import { VideoOverlayTypes } from 'rich-text-lib/constants/video';
import ConversionAssetOptionsPanel from 'rich-text-lib/components/video/ConversionAssetOptionsPanel';
import { setRefreshVideoCache } from 'tinymce-data/api/Video';
import { __HS_VIDEO, __HS_MEDIA } from '../constants/plugins';
import { appendDivToEditor, hasPlugin } from '../utils';
import { trackInteraction } from '../utils/usageTracking';
import { INSERT_CTA, OPEN_CTA_PICKER } from '../constants/usageTracking';
import { getPickerComponent } from '../utils/settings';
import { getInsertVideoHandler, getUpdateConversionAssetHandler } from './handlers';
import { addControls } from './controls';
import { addTransforms } from './transform';
import { getOpenEmbedModal } from '../hsmedia';
import { addEventListeners } from './shared';
import { getMceRootNode } from '../utils/namespace';
import { getTrackVideoInteraction } from './util';
import { AppProvider } from 'foundations-components/components/AppProvider';
import { RTEContextProvider } from 'foundations-components/transitional/context';
import { jsx as _jsx } from "react/jsx-runtime";
export const HS_VIDEO = __HS_VIDEO;
const NOOP = () => {};
let modalDomNode = null;
const getModalDomNode = editor => {
  if (!modalDomNode) {
    const container = appendDivToEditor(editor, 'video-modal-container');
    if (!container) {
      console.error('Could not append div to editor.');
      return null;
    }
    modalDomNode = container;
  }
  return modalDomNode;
};
function getOpenEditVideoDetails(editor) {
  return (target, video, onSaveCallback = NOOP) => {
    const domNode = getModalDomNode(editor);
    if (!domNode) {
      return;
    }
    const {
      player_id: playerId
    } = video;
    setRefreshVideoCache(playerId, true);
    const unmountModal = () => {
      ReactDOM.unmountComponentAtNode(domNode);
    };
    const handleCloseComplete = () => {
      tinymce.util.Delay.setEditorTimeout(editor, () => {
        editor.focus();
        const $iframe = editor.$('iframe', target);
        // Trigger iframe to reload to pick up any updates
        $iframe.attr('src', $iframe.attr('src'));
      });
    };
    const handleSaveComplete = videoFile => {
      onSaveCallback(videoFile);
      unmountModal();
      handleCloseComplete();
    };
    const HubSpotVideoPicker = getPickerComponent(editor, 'HubSpotVideoPicker', '`HubSpotVideoPicker` picker component is required for the video plugin');
    ReactDOM.render( /*#__PURE__*/_jsx(AppProvider, {
      children: /*#__PURE__*/_jsx(RTEContextProvider, {
        children: /*#__PURE__*/_jsx(HubSpotVideoPicker, {
          rootNode: getMceRootNode(),
          editingVideoId: playerId,
          onClose: unmountModal,
          onSelect: handleSaveComplete
        })
      })
    }), domNode);
    editor.on('remove', () => {
      ReactDOM.unmountComponentAtNode(domNode);
    });
  };
}
export function openVideoPicker(editor, onInsert) {
  const domNode = getModalDomNode(editor);
  const HubSpotVideoPicker = getPickerComponent(editor, 'HubSpotVideoPicker', 'HubSpotVideoPicker not found in PickerComponents');
  const handleClose = () => {
    ReactDOM.unmountComponentAtNode(domNode);
  };
  const handleInsert = media => {
    onInsert(media);
    handleClose();
  };
  ReactDOM.render( /*#__PURE__*/_jsx(AppProvider, {
    children: /*#__PURE__*/_jsx(RTEContextProvider, {
      children: /*#__PURE__*/_jsx(HubSpotVideoPicker, {
        rootNode: getMceRootNode(),
        onSelect: handleInsert,
        onClose: handleClose
      })
    })
  }), domNode);
  editor.on('remove', () => {
    ReactDOM.unmountComponentAtNode(domNode);
  });
}
const getCloseCtaPicker = editor => () => {
  const domNode = getModalDomNode(editor);
  if (!domNode) return;
  ReactDOM.unmountComponentAtNode(domNode);
};
const getHandleInsertCta = (editor, target, onInsertCallback) => {
  const closeCtaPicker = getCloseCtaPicker(editor);
  const handleUpdate = getUpdateConversionAssetHandler(editor);
  return (__html, guid, ctaInfo) => {
    if (onInsertCallback) {
      onInsertCallback(guid, ctaInfo);
    } else if (guid) {
      handleUpdate(target, {
        type: VideoOverlayTypes.CTA,
        id: guid
      });
    }
    trackInteraction(editor, 'tinymceCta', INSERT_CTA, {
      version: 'v2',
      context: 'video'
    });
    closeCtaPicker();
  };
};
const getOpenCtaPicker = editor => (target, guid, onInsertCallback) => {
  const domNode = getModalDomNode(editor);
  if (!domNode) return;
  if (typeof guid !== 'string') {
    guid = undefined;
  }
  const FilePicker = getPickerComponent(editor, 'FilePicker');
  const handleInsert = getHandleInsertCta(editor, target, onInsertCallback);
  const closeCtaPicker = getCloseCtaPicker(editor);
  const CtaPicker = getPickerComponent(editor, 'CtaPicker');
  ReactDOM.render( /*#__PURE__*/_jsx(AppProvider, {
    children: /*#__PURE__*/_jsx(RTEContextProvider, {
      children: /*#__PURE__*/_jsx(CtaPicker, {
        rootNode: getMceRootNode(),
        isVideo: true
        // do not limit to CTAs created in video flow if ungated
        ,
        includeVideoCtas: true,
        includeWebInteractives: true,
        httpClient: http,
        handleInsert: handleInsert,
        onCloseComplete: () => {
          tinymce.util.Delay.setEditorTimeout(editor, () => {
            editor.focus();
          });
        },
        portalId: PortalIdParser.get(),
        handleClose: closeCtaPicker,
        selectedGuid: guid,
        FilePicker: FilePicker
      })
    })
  }), domNode);
  editor.on('remove', () => {
    ReactDOM.unmountComponentAtNode(domNode);
  });
  trackInteraction(editor, 'tinymceCta', OPEN_CTA_PICKER, {
    version: 'v2',
    context: 'video'
  });
};
const getOpenConversionAssetOptions = editor => (target, video, conversionAsset, onSaveCallback = NOOP) => {
  const domNode = getModalDomNode(editor);
  if (!domNode) return;
  const unmountModal = () => {
    ReactDOM.unmountComponentAtNode(domNode);
  };
  const handleSubmit = asset => {
    onSaveCallback(asset);
    unmountModal();
  };
  ReactDOM.render( /*#__PURE__*/_jsx(AppProvider, {
    children: /*#__PURE__*/_jsx(RTEContextProvider, {
      children: /*#__PURE__*/_jsx(ConversionAssetOptionsPanel, {
        rootNode: getMceRootNode(),
        onSubmit: handleSubmit,
        onClose: unmountModal,
        conversionAsset: conversionAsset,
        video: video,
        trackVideoInteraction: getTrackVideoInteraction(editor)
      })
    })
  }), domNode);
  editor.on('remove', () => {
    ReactDOM.unmountComponentAtNode(domNode);
  });
};
const getPopoverOptions = editor => {
  return {
    onEditDetails: getOpenEditVideoDetails(editor),
    onUpdateConversionAsset: getUpdateConversionAssetHandler(editor),
    onOpenCtaPicker: getOpenCtaPicker(editor),
    onOpenConversionAssetPanel: getOpenConversionAssetOptions(editor)
  };
};
function getOpenVideoPicker(editor) {
  return target => {
    const onInsertVideo = getInsertVideoHandler(editor, target);
    openVideoPicker(editor, onInsertVideo);
  };
}
function combinedVideoPlugin(editor) {
  if (!hasPlugin(editor, __HS_MEDIA)) {
    throw new Error(`[tinymce-plugins] ${HS_VIDEO} requires the ${__HS_MEDIA} plugin`);
  }
  addControls(editor, HS_VIDEO, getOpenVideoPicker(editor), getOpenEmbedModal(editor), getPopoverOptions(editor));
  addTransforms(editor);
  addEventListeners(editor);
}
tinymce.PluginManager.add(HS_VIDEO, combinedVideoPlugin);